import { apiClient } from "@/api";

/** What `POST /v1/rehearsal/sessions` returns. */
export interface SessionResponse {
  readonly session_id: string;
  readonly token: string;
}

/** A ref-shaped cache. `useRef` satisfies it; a test passes a plain object. */
export interface SessionCache {
  current: { key: number; promise: Promise<SessionResponse> } | null;
}

/**
 * One session per mount attempt, however many times the effect runs.
 *
 * Creating the session inside an effect means React StrictMode's
 * double-invoke sends the POST twice, and the effect's `cancelled` guard
 * cannot help: it stops the discarded run's state updates and cannot
 * un-send a request already in flight. Each response carries
 * `Set-Cookie: animachord_rehearsal=...` under a fixed name, so the
 * second overwrites the first and one of the two sessions is orphaned.
 *
 * ## Why the cache is passed in rather than held here
 *
 * The first version of this kept the cache in module scope, and that is
 * too durable by exactly one lifetime. Module state outlives the page:
 * a visitor who leaves `/rehearsal` and comes back inside the same SPA
 * session gets a *new component* and a *reset* `mountToken`, and would
 * have been handed the previous visit's cached session — dead, and
 * failing in a way indistinguishable from the bug this was written to
 * prevent. An existing Retry test caught it, which is the only reason
 * it is not still in here.
 *
 * A `useRef` is the right lifetime. StrictMode's double-invoke runs the
 * effect, its cleanup, and the effect again **on the same instance**, so
 * a ref survives it; a genuine remount makes a new instance with a fresh
 * ref and therefore a genuinely new session. Passing the cache in keeps
 * that lifetime with the component and still leaves this a plain
 * function a test can drive.
 *
 * Keyed on `mountToken` as well, so the Retry button gets a new session
 * rather than replaying the dead one.
 *
 * ## Scope
 *
 * This is a second line of defence, not the fix for animachord#61. The
 * page failed because the rehearsal socket accepted without echoing the
 * `bearer.<token>` subprotocol the console always offers, which browsers
 * are required to reject. The duplicate session was real, wasteful, and
 * not what broke beat 0.
 */
export function createSession(
  cache: SessionCache,
  key: number,
): Promise<SessionResponse> {
  const cached = cache.current;
  if (cached && cached.key === key) return cached.promise;

  const promise = apiClient
    .post<SessionResponse>("/v1/rehearsal/sessions", { body: {} })
    .catch((error: unknown) => {
      // A rejected promise left in the cache would make Retry replay the
      // same failure forever without reaching the network, turning a
      // transient outage into a permanently broken page.
      if (cache.current?.key === key) cache.current = null;
      throw error;
    });

  cache.current = { key, promise };
  return promise;
}
